import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import useMultipleExams from "@features/exam/contexts/useMultipleExams";
import {
	isIntroScreen,
	isIntroScreenWithoutAppBar,
} from "@features/session/helpers/intro-screens";
import useCurrentSession from "@features/session/hooks/useCurrentSession";
import useSessionStore from "@features/session/hooks/useSessionStore";
import { useAppStore } from "@hooks/useAppStore";
import type { TimerScope } from "@memorang/ui/components/StudySession/StudySessionAppbar/SummativeAppbar/Pte";
import { useDevice } from "@memorang/ui/useDevice";
import { useEffect, useMemo } from "react";
import {
	calculateTotalTimeRemaining,
	resolveTimerDuration,
} from "../helpers/appbar";
import { hasListeningItems } from "../helpers/content";
import { deriveItemTimingPlan } from "../helpers/item-timing-plan";
import {
	getSectionDurationSeconds,
	getSectionElapsedKey,
} from "../helpers/section-timing";
import { hasAnsweredAnyItem } from "../helpers/session";
import useEditorStore from "../hooks/useEditorStore";
import usePausableCountdownWindow from "../hooks/usePausableCountdownWindow";
import useResponseControlsStore from "../hooks/useResponseControlsStore";
import useCountDown from "../hooks/useTimer";
import { useBlockAnsweredState } from "./useBlockAnsweredState";
import useItemAnsweredState from "./useItemAnsweredState";

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
const useSessionAppBarState = (showDialog?: boolean) => {
	const sessionNumericId = useSessionStore((s) => s.numericId);
	const sessionItems = useSessionStore((s) => s.sessionItems);
	const inReviewMode = useSessionStore((s) => s.inReviewMode);
	const isSummativeTest = useSessionStore((s) => s.isSummativeTest);
	const totalTime = useSessionStore((s) => s.totalTime);
	const timeElapsed = useSessionStore((s) => s.timeElapsed);
	const blockStates = useSessionStore((s) => s.blockStates);
	const itemStates = useSessionStore((s) => s.itemStates);
	const startTime = useSessionStore((s) => s.startTime);
	const serverExamBreak = useSessionStore((s) => s.serverExamBreak);
	const examName = useSessionStore((s) => s.examName);
	const sessionSubType = useSessionStore((s) => s.sessionSubType);
	const answers = useSessionStore((s) => s.answers);
	const itemViewedTimestamps = useSessionStore((s) => s.itemViewedTimestamps);
	const sessionId = useSessionStore((s) => s.id);
	const sessionTime = useSessionStore((s) => s.sessionTime);
	const currentProgress = useSessionStore((s) => s.currentProgress);
	const isDiagnostic = useSessionStore((s) => s.isDiagnostic);
	const sessionType = useSessionStore((s) => s.sessionType);
	const collectionId = useSessionStore((s) => s.collectionId);
	const breaks = useSessionStore((s) => s.breaks);
	const isBreakOngoing = useSessionStore((s) => s.isBreakOngoing);

	const diagnosticLabel = useAppStore((s) => s.exam.diagnosticLabel);
	const shouldShowFlightCalculator = useAppStore(
		(s) => s.sessionView?.showFlightCalculator,
	);
	const editorRef = useEditorStore((store) => store.editorRef);

	const {
		currentItem,
		currentBlockIndex,
		currentItemIndex,
		currentBlock,
		numTotalItems,
		numSessionItems: numSections,
		currentPart,
		currentPartIndex,
		numTotalParts,
		sectionIndexWithinPart,
		displayItemIndex,
	} = useCurrentSession();
	const { isAnswered } = useItemAnsweredState(currentItem);
	const { isAnswered: isBlockAnswered, numItemsLeft } =
		useBlockAnsweredState(currentBlock);

	const sectionName = currentItem?.metadata?.sectionName ?? "";

	const {
		currentExamContext: {
			projectId,
			organizationId,
			examId,
			examName: contextExamName,
		},
	} = useExamContext();

	const { username } = useAuthContext();
	const { relatedExams } = useMultipleExams();
	const hasMultipleExams = relatedExams.length >= 1;
	const { isMobile } = useDevice();

	const currentItemId = currentItem?.id;
	const currentItemState = itemStates?.get(currentItemId!);

	const currentBlockState = blockStates.get(currentBlockIndex);
	const markedItems = currentBlockState?.markedItemsForReview;
	const highlightedItems = currentBlockState?.highlightedItems;
	const isMarked =
		currentItem?.answer?.marked || markedItems?.includes(currentItemId!);
	const highlightedHtml =
		currentItem?.answer?.highlight || highlightedItems?.get(currentItemId!);

	const lastBreak = breaks?.length
		? breaks[breaks.length - 1]
		: serverExamBreak;
	const breakTimeTakenInSeconds = lastBreak?.totalBreakTimeTakenInSeconds;
	const isOlxSession = examName?.includes("OLX");

	const currentSection = currentItem?.metadata?.section;
	const currentPartNumber = currentPart?.partId ?? currentPartIndex + 1;

	const timingPlan = deriveItemTimingPlan({
		item: currentItem,
		isSummativeTest,
		inReviewMode,
	});
	const {
		hasItemTimer,
		timeLimitSeconds,
		timerWaitsForAudioCompletion,
		micDelaySecondsAfterAudio,
		prepSecondsBeforeTimer,
	} = timingPlan;

	const isItemAudioComplete = useResponseControlsStore((s) =>
		Boolean(currentItemId && s.audioCompletedItems[currentItemId]),
	);
	const itemTimerAwaitingAudio =
		timerWaitsForAudioCompletion && !isItemAudioComplete;

	const isHoldingForMicDelay = usePausableCountdownWindow({
		shouldHold: micDelaySecondsAfterAudio > 0 && isItemAudioComplete,
		holdSeconds: micDelaySecondsAfterAudio,
		resetKey: currentItemId,
		isPaused: showDialog,
	});

	const storedItemTimeElapsed = currentItem?.answer?.timeElapsed ?? 0;

	const itemTimerHasPrep = prepSecondsBeforeTimer > 0;
	const storedPrepDone =
		itemTimerHasPrep && storedItemTimeElapsed >= prepSecondsBeforeTimer;
	const storedTimerElapsed = itemTimerHasPrep
		? Math.max(0, storedItemTimeElapsed - prepSecondsBeforeTimer)
		: storedItemTimeElapsed;

	const itemTimerWaitsForPrep = itemTimerHasPrep && !storedPrepDone;

	const isHoldingForPrep = usePausableCountdownWindow({
		shouldHold: itemTimerWaitsForPrep,
		holdSeconds: prepSecondsBeforeTimer,
		resetKey: currentItemId,
		isPaused: showDialog,
	});

	const sectionDurationSeconds = useMemo(
		() =>
			getSectionDurationSeconds(
				sessionItems,
				currentPartNumber,
				currentSection,
			),
		[sessionItems, currentPartNumber, currentSection],
	);

	const sectionElapsedSeconds = useSessionStore((s) => s.sectionElapsedSeconds);

	// Captured once per section: the resumed session's stored elapsed seeds
	// the section timer; navigating within the section must not restart it.
	// biome-ignore lint/correctness/useExhaustiveDependencies: capture on section change only
	const storedSectionElapsed = useMemo(
		() =>
			sectionElapsedSeconds[
				getSectionElapsedKey(currentPartNumber, currentSection ?? "")
			] ?? 0,
		[currentBlockIndex],
	);

	const timerDuration = resolveTimerDuration({
		itemTimeLimit: hasItemTimer ? timeLimitSeconds : undefined,
		itemTimeElapsed: storedTimerElapsed,
		totalTime: sectionDurationSeconds || totalTime,
	});

	const totalTimeRemaining = hasItemTimer
		? timerDuration
		: sectionDurationSeconds
			? Math.max(0, sectionDurationSeconds - storedSectionElapsed)
			: calculateTotalTimeRemaining({
					isOlxSession,
					startTime,
					isBreakOngoing,
					totalTime: timerDuration,
					sessionTime,
					breakTimeTakenInSeconds,
				});
	const isTimeExpired = totalTimeRemaining <= 0;

	const timerScope: TimerScope = hasItemTimer
		? "item"
		: sectionDurationSeconds > 0
			? "section"
			: "test";

	const itemTimerShouldHold =
		hasItemTimer &&
		(itemTimerAwaitingAudio || isHoldingForMicDelay || isHoldingForPrep);

	const timerShouldRun =
		isSummativeTest &&
		!inReviewMode &&
		!isBreakOngoing &&
		!isIntroScreenWithoutAppBar(currentItem) &&
		!showDialog &&
		!itemTimerShouldHold;

	const {
		isNegative,
		totalSeconds: timerTotalSeconds,
		hours,
		minutes,
		seconds,
		start,
		pause,
		resume,
	} = useCountDown((totalTimeRemaining || 0) * 1000, timerShouldRun);

	// biome-ignore lint/correctness/useExhaustiveDependencies: restart only on item change
	useEffect(() => {
		if (!hasItemTimer) {
			return;
		}
		start(timerDuration * 1000);
		if (!timerShouldRun || itemTimerWaitsForPrep) {
			pause();
		}
	}, [currentItemId]);

	const updateItemTimeElapsed = useSessionStore((s) => s.updateItemTimeElapsed);
	const prepElapsedSeconds =
		itemTimerHasPrep && !isHoldingForPrep ? prepSecondsBeforeTimer : 0;
	const itemTimeElapsed =
		hasItemTimer && typeof timeLimitSeconds === "number"
			? prepElapsedSeconds +
				Math.max(0, timeLimitSeconds - Math.max(0, timerTotalSeconds))
			: 0;
	useEffect(() => {
		updateItemTimeElapsed(itemTimeElapsed);
	}, [itemTimeElapsed, updateItemTimeElapsed]);

	const updateSectionElapsed = useSessionStore((s) => s.updateSectionElapsed);
	const sectionTimeElapsed =
		!hasItemTimer && sectionDurationSeconds > 0
			? Math.max(0, sectionDurationSeconds - Math.max(0, timerTotalSeconds))
			: 0;

	useEffect(() => {
		if (hasItemTimer || sectionDurationSeconds <= 0) {
			return;
		}
		updateSectionElapsed(
			currentPartNumber,
			currentSection ?? "",
			sectionTimeElapsed,
		);
	}, [
		hasItemTimer,
		sectionDurationSeconds,
		currentPartNumber,
		currentSection,
		sectionTimeElapsed,
		updateSectionElapsed,
	]);

	const formattedTime = `${isNegative ? "-" : ""}${hours}:${minutes}:${seconds}`;
	const hasAnsweredAtLeastOneItem =
		hasAnsweredAnyItem(answers) || isBlockAnswered;
	const blocks = [...blockStates.values()];
	const hasParts = numTotalParts > 1;
	const partScopedBlocks = currentPart
		? currentPart.blockIndices.map(
				(blockIndex) => blockStates.get(blockIndex) ?? { currentProgress: 0 },
			)
		: blocks;
	const showToolbar = inReviewMode ? false : !isMobile && isSummativeTest;

	const skillName = currentItem.metadata?.skill ?? "";
	const currentPartName = isIntroScreen(currentItem)
		? undefined
		: (currentPart?.partName ?? "");

	const hasListeningItemsResult = useMemo(() => {
		return hasListeningItems(sessionItems ?? []);
	}, [sessionItems]);

	return {
		sessionNumericId,
		sessionItems,
		inReviewMode,
		isSummativeTest,
		totalTime,
		timeElapsed,
		blockStates,
		itemStates,
		startTime,
		serverExamBreak,
		examName,
		sessionSubType,
		answers,
		itemViewedTimestamps,
		sessionId,
		sessionTime,
		currentProgress,
		isDiagnostic,
		sessionType,
		collectionId,
		breaks,
		isBreakOngoing,
		diagnosticLabel,
		shouldShowFlightCalculator,
		editorRef,
		currentItem,
		currentBlockIndex,
		currentItemIndex,
		currentBlock,
		numTotalItems,
		numSections,
		projectId,
		organizationId,
		examId,
		contextExamName,
		username,
		isMobile,
		currentItemId,
		currentItemState,
		numItemsLeft,
		isAnswered,
		isMarked,
		highlightedHtml,
		isOlxSession,
		totalTimeRemaining,
		timerTotalSeconds,
		timerScope,
		isTimeExpired,
		isNegative,
		formattedTime,
		hasAnsweredAtLeastOneItem,
		blocks,
		hasParts,
		partScopedBlocks,
		currentPartIndex,
		numTotalParts,
		sectionIndexWithinPart,
		showToolbar,
		displayItemIndex,
		skillName,
		hasMultipleExams,
		pause,
		resume,
		hasListeningItems: hasListeningItemsResult,
		currentPartName,
		currentSection,
		sectionName,
	};
};

export default useSessionAppBarState;
